import { useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperclip } from 'react-icons/fa';
import type { FileAttachment as FileAttachmentType } from '../../types';
import {
  DEFAULT_MAX_FILES,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_ACCEPTED_TYPES,
  filesToAttachments,
} from '../../services/fileService';
import { Button, Box } from '@chakra-ui/react';

interface FileAttachmentProps {
  onFilesSelected: (files: FileAttachmentType[]) => void;
  currentFileCount: number;
  maxFiles?: number;
  maxFileSize?: number; // bytes
  acceptedTypes?: string[]; // MIME types
  disabled?: boolean;
  onError?: (error: string) => void; // エラーを親コンポーネントに通知
}

export const FileAttachment = ({
  onFilesSelected,
  currentFileCount,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  disabled = false,
  onError,
}: FileAttachmentProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    onError?.('');

    const result = await filesToAttachments(
      files,
      maxFileSize,
      acceptedTypes,
      maxFiles,
      currentFileCount
    );

    if (result.error) {
      onError?.(result.error);
    } else if (result.attachments.length > 0) {
      onFilesSelected(result.attachments);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box position="absolute" w={0} h={0} overflow="hidden">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileChange}
          disabled={disabled}
          tabIndex={-1}
        />
      </Box>

      <Button
        type="button"
        variant="secondary"
        size="md"
        onClick={handleButtonClick}
        disabled={disabled || currentFileCount >= maxFiles}
        title={t('fileAttachment.attachFile')}
        aria-label={t('fileAttachment.attachFile')}
        p={2}
        minW="auto"
      >
        <FaPaperclip aria-hidden="true" />
      </Button>
    </Box>
  );
};
